import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';

import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {useSystemAccounts} from 'src/hooks/account/system-accounts';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';

import CheckBox from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {MAX_GROUP_NAME_LENGTH} from '../constants';
import {movePrimaryAccountManagerToEnd} from '../utils/utils';

import {GroupSettingsSkeleton} from './group-settings.sleleton';

import type {AccountGroupFullInfo} from 'src/openapi/admin/models/AccountGroupFullInfo';
import type {AccountManagerInfo} from 'src/openapi/admin/models/AccountManagerInfo';
import type {Error} from 'src/openapi/admin/models/Error';
import type {CheckBoxChangeData, OnComboBoxChange} from 'src/typedef';


const OVERRIDE_UNTRACKED_ACCOUNT_GROUP_PERMISSIONS = (
    <span>
        News & Files view permissions of a group in the untracked account can't be overridden
    </span>
);

type Props = {
    accountId: number,
    isLoading: boolean,
    loadingError: Error | null,
    data: AccountGroupFullInfo,
    setData: React.Dispatch<React.SetStateAction<AccountGroupFullInfo>>,
    accountTeamMembers: AccountManagerInfo[],
    viewType: 'New' | 'Existing',
    disabled?: boolean,
};

export const GroupSettingsTab = (props: Props) => {
    const {
        accountId,
        isLoading,
        loadingError,
        data,
        setData,
        viewType,
        accountTeamMembers,
        disabled,
    } = props;
    const {hierarchy} = useSelector(selectAccountDetails);
    const {isAdminAccount} = useSystemAccounts();
    const {groupChange} = useEventTracking();
    const {getUserTypeByCode} = useUserTypes();
    const {getUserStatusByCode} = useUserStatuses();

    const changeGroupName = useCallback((name: string) => {
        setData(prev => ({...prev, name}));
        groupChange({viewType, parameterName: 'Group Name'});
    }, [viewType, groupChange, setData]);

    const changeGroupOwner = useCallback<OnComboBoxChange>(ownerId => {
        setData(prev => ({...prev, ownerId}));
        groupChange({viewType, parameterName: 'Group Owner'});
    }, [viewType, groupChange, setData]);

    const changeLimitCollaboration = useCallback(({event}: CheckBoxChangeData) => {
        setData(prev => ({...prev, groupOnlyCollaboration: event.currentTarget.checked}));
        groupChange({viewType, parameterName: 'Limit collaboration to within this Group'});
    }, [viewType, groupChange, setData]);

    const changeAnonymousSharing = useCallback(({event}: CheckBoxChangeData) => {
        setData(prev => ({...prev, anonymousSharingEnabled: event.currentTarget.checked}));
        groupChange({viewType, parameterName: 'Enable anonymous sharing'});
    }, [viewType, groupChange, setData]);

    const overridePrivacyPermission = useCallback(({event}: CheckBoxChangeData) => {
        setData(prev => ({
            ...prev,
            ...event.currentTarget.checked ? {
                overridePrivacyPermission: false,
                accountPrivacyEnabled: false,
                groupPrivacyEnabled: false,
                userPrivacyEnabled: false,
            } : {overridePrivacyPermission: true},
        }));
        groupChange({viewType, parameterName: 'Use default settings'});
    }, [viewType, groupChange, setData]);

    const changeCanPostToEntireAccount = useCallback(({event}: CheckBoxChangeData) => {
        setData(prev => ({...prev, accountPrivacyEnabled: event.currentTarget.checked}));
        groupChange({viewType, parameterName: 'Can post to entire account'});
    }, [viewType, groupChange, setData]);

    const changeCanPostToMyGroup = useCallback(({event}: CheckBoxChangeData) => {
        setData(prev => ({...prev, groupPrivacyEnabled: event.currentTarget.checked}));
        groupChange({viewType, parameterName: 'Can post to My Group'});
    }, [viewType, groupChange, setData]);

    const changeCanPostToOnlyMe = useCallback(({event}: CheckBoxChangeData) => {
        setData(prev => ({...prev, userPrivacyEnabled: event.currentTarget.checked}));
        groupChange({viewType, parameterName: 'Can post to Only Me'});
    }, [viewType, groupChange, setData]);

    const changeMyGroupPrivacy = useCallback(({event}: CheckBoxChangeData) => {
        setData(prev => ({...prev, enableMyGroupPrivacy: event.currentTarget.checked}));
        groupChange({viewType, parameterName: 'Enable My Group Privacy for My Fields'});
    }, [viewType, groupChange, setData]);

    const changeNotes = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, notes: event.target.value}));
    }, [setData]);

    const blurNotes = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, notes: event.target.value.trim()}));
        groupChange({viewType, parameterName: 'Notes'});
    }, [setData, groupChange, viewType]);

    return (
        <WidthItem width={827}>
            <Skeleton shown={isLoading} view={<GroupSettingsSkeleton/>}>
                <ServerResponseHandler
                    serverError={loadingError}
                    smallView={true}>
                    <Group direction="v">
                        <FormInput
                            width={827}
                            readOnly={disabled}
                            label={<RequiredMark label="Group Name"/>}
                            value={data.name}
                            maxLength={MAX_GROUP_NAME_LENGTH}
                            onBlur={changeGroupName}
                        />
                        <div>
                            <Label>Group Owner</Label>
                            <WidthItem width={827}>
                                <ComboBox
                                    value={data.ownerId}
                                    onChange={changeGroupOwner}
                                    name="ownerId"
                                    disabled={disabled}>
                                    {movePrimaryAccountManagerToEnd(accountTeamMembers).map(e => (
                                        <ComboboxItem
                                            key={e.id}
                                            value={e.id}
                                            caption={`${e.email || e.fullName || ''} (${getUserTypeByCode(e.userType)?.description
                                            } ${getUserStatusByCode(e.userStatus)?.description})`}
                                        />
                                    ))}
                                </ComboBox>
                            </WidthItem>
                        </div>
                        <div>
                            <Group direction="v" gap="sm">
                                <Group direction="h" gap="xxxl">
                                    <div>
                                        <Label>Searches & Lists & Workspaces Permissions.</Label>
                                        <CheckBox
                                            disabled={disabled}
                                            label="Limit collaboration to within this Group"
                                            checked={data.groupOnlyCollaboration}
                                            onCheckBoxChange={changeLimitCollaboration}
                                        />
                                    </div>
                                    <div>
                                        <Label>My Fields Permissions</Label>
                                        <CheckBox
                                            disabled={disabled}
                                            label="Enable 'My Group' Privacy for My Fields"
                                            checked={data.enableMyGroupPrivacy}
                                            onCheckBoxChange={changeMyGroupPrivacy}
                                        />
                                    </div>
                                </Group>
                                {isAdminAccount(accountId) && (
                                    <CheckBox
                                        disabled={disabled}
                                        label="Enable anonymous sharing"
                                        checked={data.anonymousSharingEnabled}
                                        name="anonymousSharingEnabled"
                                        onCheckBoxChange={changeAnonymousSharing}
                                    />
                                )}
                            </Group>
                        </div>
                        <div>
                            <Label>
                                {hierarchy?.current?.isUntracked ? (
                                    <Info type={{popover: OVERRIDE_UNTRACKED_ACCOUNT_GROUP_PERMISSIONS}}>
                                        News & Files View Permissions
                                    </Info>
                                ) : 'Notes & Files View Permissions'}
                            </Label>
                            <CheckBox
                                label="Use default settings"
                                checked={hierarchy?.current?.isUntracked || !data.overridePrivacyPermission}
                                disabled={hierarchy?.current?.isUntracked || disabled}
                                onCheckBoxChange={overridePrivacyPermission}
                            />
                            <CheckBox
                                label="Can post to entire account"
                                checked={data.accountPrivacyEnabled}
                                disabled={!data.overridePrivacyPermission || disabled}
                                onCheckBoxChange={changeCanPostToEntireAccount}
                            />
                            <CheckBox
                                label="Can post to My Group"
                                checked={data.groupPrivacyEnabled}
                                disabled={!data.overridePrivacyPermission || disabled}
                                onCheckBoxChange={changeCanPostToMyGroup}
                            />
                            <CheckBox
                                label="Can post to Only Me"
                                checked={data.userPrivacyEnabled}
                                disabled={!data.overridePrivacyPermission || disabled}
                                onCheckBoxChange={changeCanPostToOnlyMe}
                            />
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <WidthItem width={827}>
                                <Textarea
                                    disabled={disabled}
                                    maxLength={3000}
                                    resizable={false}
                                    value={data.notes || ''}
                                    onChange={changeNotes}
                                    onBlur={blurNotes}
                                />
                            </WidthItem>
                        </div>
                    </Group>
                </ServerResponseHandler>
            </Skeleton>
        </WidthItem>
    );
};
